import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject, RefObject } from 'react';
import { createPortal } from 'react-dom';
import gsap from 'gsap';
import { BOX, HOT, ZOOM_BOX, computeGeo, toLocal } from './heroGeometry';
import type { Geo } from './heroGeometry';

// Production 3x assets (4707 × 3006 = exactly 3 × the 1569 × 1002 geometry space).
const IMG_A = 'https://storage.helloreaddy.io/project_files/2fa078c8-fdd1-459f-9704-eede5204918a/1e982407-9150-4b53-9fdb-81f66c0b8fb9_compressed_MENDWELL_Hero_A_3x.webp';
const IMG_B = 'https://storage.helloreaddy.io/project_files/2fa078c8-fdd1-459f-9704-eede5204918a/f455c3e7-1091-4e44-a278-8d4605d3f320_compressed_MENDWELL_Hero_B_XRay_3x.webp';
// Rollback (previous 1x pair) — swap back into IMG_A / IMG_B if validation fails.
export const IMG_A_LEGACY = 'https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/f1bf576b-029d-4231-a054-d104ffd279e7_mendwell-a.webp';
export const IMG_B_LEGACY = 'https://public.readdy.ai/ai/img_res/sandbox/f2895d2f16dd8702941ce32547f07592/f1bf576b-029d-4231-a054-d104ffd279e7_mendwell-b.webp';

type DebugMode = 'off' | 'full' | 'fixed' | 'pointer';

export interface XrayControl {
  arm: (on: boolean, withDemo?: boolean) => void;
  /** Scroll-driven scan, p in [0,1]. Fully reversible. */
  scan: (p: number) => void;
}

interface Props {
  mediaRef: RefObject<HTMLDivElement | null>;
  controlRef: MutableRefObject<XrayControl | null>;
}

interface DebugInfo {
  aLoaded: boolean;
  bLoaded: boolean;
  armed: boolean;
  visible: boolean;
  img: string;
  mask: string;
  hot: boolean;
}

// Debug panel only appears with ?xray-debug=1 in the URL — never in the normal published view.
const DEBUG_ENABLED = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('xray-debug');

export default function HeroXray({ mediaRef, controlRef }: Props) {
  const rootRef = useRef<HTMLDivElement>(null);
  const maskRef = useRef<HTMLDivElement>(null);
  const imgARef = useRef<HTMLImageElement>(null);
  const imgBRef = useRef<HTMLImageElement>(null);
  const geoRef = useRef<Geo | null>(null);
  // Single, never-replaced position object. All tweens target this exact object.
  const pos = useRef({ x: 0, y: 0, r: 0 });
  const quick = useRef<{ x: gsap.QuickToFunc; y: gsap.QuickToFunc; r: gsap.QuickToFunc } | null>(null);
  const demoRef = useRef<gsap.core.Timeline | null>(null);
  const demoDone = useRef(false);
  const armedRef = useRef(false);
  const hotRef = useRef(false);
  const forceHot = useRef(false);
  const pointerType = useRef('mouse');
  const debugModeRef = useRef<DebugMode>('off');
  const lastPointerImg = useRef<{ x: number; y: number } | null>(null);
  const loaded = useRef({ a: false, b: false });
  const [geo, setGeo] = useState<Geo | null>(null);
  const [hot, setHot] = useState(false);
  const [, setArmed] = useState(false);
  const [, setTouched] = useState(false);
  const [reduced, setReduced] = useState(false);
  const [debugMode, setDebugMode] = useState<DebugMode>('off');
  const [dbg, setDbg] = useState<DebugInfo | null>(null);

  const measure = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const g = computeGeo(el.clientWidth, el.clientHeight);
    geoRef.current = g;
    setGeo(g);
  }, []);

  const activeRadius = () => {
    const g = geoRef.current;
    return g ? (ZOOM_BOX.x1 - ZOOM_BOX.x0) * g.s * 0.34 : 0;
  };

  const cancelDemo = () => {
    demoRef.current?.kill();
    demoRef.current = null;
    forceHot.current = false;
  };

  const runDemo = () => {
    const g = geoRef.current;
    if (!g || demoRef.current || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    demoDone.current = true;
    // Auto reveal: open the full X-Ray over the whole washer, flag the drain pump,
    // narrow focus onto the pump, then close so the user can scan by hand.
    const c = toLocal(g, (BOX.x0 + BOX.x1) / 2, (BOX.y0 + BOX.y1) / 2);
    const tl0 = toLocal(g, BOX.x0, BOX.y0);
    const h = toLocal(g, HOT.x, HOT.y);
    // Mask is solid to 55% of r — size it so the solid core covers the whole washer
    const full = (Math.hypot(c.x - tl0.x, c.y - tl0.y) / 0.55) * 1.05;
    // Mutate in place — never reassign pos.current (quickTo tweens are bound to this object)
    Object.assign(pos.current, { x: c.x, y: c.y, r: 0 });
    demoRef.current = gsap.timeline({
      delay: 0.3,
      onComplete: () => { demoRef.current = null; forceHot.current = false; },
    })
      .to(pos.current, { r: full, duration: 0.9, ease: 'power2.out' })
      .call(() => { forceHot.current = true; }, [], '+=0.15')
      .to(pos.current, { x: h.x, y: h.y, r: activeRadius() * 1.15, duration: 0.9, ease: 'power2.inOut' }, '+=1.1')
      .to(pos.current, { r: 0, duration: 0.55, ease: 'power2.in' }, '+=1.2')
      .call(() => { forceHot.current = false; }, [], '-=0.4');
  };

  useEffect(() => {
    controlRef.current = {
      scan: (raw) => {
        const g = geoRef.current;
        if (!g) return;
        cancelDemo();
        const p = Math.min(Math.max(raw, 0), 1);
        const c = toLocal(g, (BOX.x0 + BOX.x1) / 2, (BOX.y0 + BOX.y1) / 2);
        const tl0 = toLocal(g, BOX.x0, BOX.y0);
        const h = toLocal(g, HOT.x, HOT.y);
        const full = (Math.hypot(c.x - tl0.x, c.y - tl0.y) / 0.55) * 1.05;
        const focus = activeRadius() * 1.15;
        const ease = (t: number) => t * t * (3 - 2 * t);
        let x = c.x; let y = c.y; let r = 0;
        if (p < 0.35) {
          r = full * ease(p / 0.35);
        } else if (p < 0.55) {
          r = full;
        } else if (p < 0.8) {
          const t = ease((p - 0.55) / 0.25);
          x = c.x + (h.x - c.x) * t;
          y = c.y + (h.y - c.y) * t;
          r = full + (focus - full) * t;
        } else {
          x = h.x; y = h.y;
          r = focus * (1 - ease((p - 0.8) / 0.2));
        }
        forceHot.current = p > 0.3 && p < 0.95;
        Object.assign(pos.current, { x, y, r });
      },
      arm: (on, withDemo = false) => {
        if (armedRef.current === on) return;
        armedRef.current = on;
        setArmed(on);
        if (!on) {
          // Let a running auto-demo finish even if the user scrolls past the
          // diagnostic hold quickly; only stop manual scanning.
          if (demoRef.current) return;
          // Do NOT killTweensOf(pos) — that destroys the quickTo tweens permanently.
          quick.current?.r(0);
        } else if (withDemo) {
          runDemo();
        }
      },
    };
    return () => { controlRef.current = null; };
  });

  // Root-local point → media-local point using the live, GSAP-transformed rect.
  const toMedia = (cx: number, cy: number) => {
    const m = mediaRef.current;
    const root = rootRef.current;
    if (!m || !root || !root.clientWidth) return { x: cx, y: cy };
    const rr = root.getBoundingClientRect();
    const mr = m.getBoundingClientRect();
    const s = mr.width / root.clientWidth || 1;
    return { x: (cx - (mr.left - rr.left)) / s, y: (cy - (mr.top - rr.top)) / s };
  };
  const toMediaRef = useRef(toMedia);
  toMediaRef.current = toMedia;

  const aim = (lx: number, ly: number) => {
    const g = geoRef.current;
    const q = quick.current;
    if (!g || !q) return;
    lastPointerImg.current = { x: (lx - g.ox) / g.s, y: (ly - g.oy) / g.s };
    const tl = toLocal(g, BOX.x0, BOX.y0);
    const br = toLocal(g, BOX.x1, BOX.y1);
    const m = 24;
    if (lx < tl.x - m || lx > br.x + m || ly < tl.y - m || ly > br.y + m) {
      q.r(0);
      return;
    }
    q.x(Math.min(Math.max(lx, tl.x), br.x));
    q.y(Math.min(Math.max(ly, tl.y), br.y));
    q.r(activeRadius());
  };
  const aimRef = useRef(aim);
  aimRef.current = aim;

  useEffect(() => {
    const isReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setReduced(isReduced);
    measure();
    if (imgARef.current?.complete && imgARef.current.naturalWidth) loaded.current.a = true;
    if (imgBRef.current?.complete && imgBRef.current.naturalWidth) loaded.current.b = true;
    const dur = isReduced ? 0.01 : 0.2;
    quick.current = {
      x: gsap.quickTo(pos.current, 'x', { duration: dur, ease: 'power3.out' }),
      y: gsap.quickTo(pos.current, 'y', { duration: dur, ease: 'power3.out' }),
      r: gsap.quickTo(pos.current, 'r', { duration: isReduced ? 0.01 : 0.25, ease: 'power2.out' }),
    };

    let frame = 0;
    const tick = () => {
      const el = maskRef.current;
      const g = geoRef.current;
      if (!el || !g) return;
      const mode = debugModeRef.current;
      let { x, y, r } = pos.current;
      if (mode === 'fixed') {
        const c = toLocal(g, (BOX.x0 + BOX.x1) / 2, (BOX.y0 + BOX.y1) / 2);
        x = c.x; y = c.y; r = activeRadius();
      }
      let maskStr = 'none';
      let visible: boolean;
      if (mode === 'full') {
        visible = true;
      } else {
        visible = r >= 1;
        maskStr = `radial-gradient(circle ${Math.max(r, 0.5).toFixed(1)}px at ${x.toFixed(1)}px ${y.toFixed(1)}px, #000 55%, transparent 100%)`;
      }
      // Direct, fully-resolved values (no CSS variables) for both prefixed and standard mask
      el.style.setProperty('-webkit-mask-image', maskStr);
      el.style.setProperty('mask-image', maskStr);
      el.style.opacity = visible ? '1' : '0';

      const hs = toLocal(g, HOT.x, HOT.y);
      const near = mode !== 'full' && r > 10
        && (forceHot.current || Math.hypot(x - hs.x, y - hs.y) < r * 0.75);
      if (near !== hotRef.current) {
        hotRef.current = near;
        setHot(near);
      }

      if (DEBUG_ENABLED) {
        frame += 1;
        if (frame % 6 === 0) {
          const p = lastPointerImg.current;
          setDbg({
            aLoaded: loaded.current.a,
            bLoaded: loaded.current.b,
            armed: armedRef.current,
            visible,
            img: p ? `${Math.round(p.x)}, ${Math.round(p.y)}` : '—',
            mask: mode === 'full' ? 'none (full)' : `${Math.round((x - g.ox) / g.s)}, ${Math.round((y - g.oy) / g.s)} · r ${Math.round(r / g.s)}src`,
            hot: near,
          });
        }
      }
    };
    gsap.ticker.add(tick);

    const onResize = () => {
      measure();
      cancelDemo();
      pos.current.r = 0;
      quick.current?.r(0);
    };
    window.addEventListener('resize', onResize);

    let inside = false;
    let lastXY: { x: number; y: number } | null = null;
    const onWinMove = (e: PointerEvent) => {
      // Ignore sub-pixel jitter / synthetic moves so a resting mouse doesn't kill the demo
      const moved = lastXY ? Math.hypot(e.clientX - lastXY.x, e.clientY - lastXY.y) : 0;
      lastXY = { x: e.clientX, y: e.clientY };
      if (demoRef.current && moved < 4) return;
      if (e.pointerType !== 'mouse') return;
      pointerType.current = 'mouse';
      const root = rootRef.current;
      // Hover scanning disabled — the X-Ray is scroll-driven now.
      const allowed = debugModeRef.current === 'pointer';
      if (!root || !allowed) return;
      const rect = root.getBoundingClientRect();
      const lx = e.clientX - rect.left;
      const ly = e.clientY - rect.top;
      const within = lx >= 0 && ly >= 0 && lx <= rect.width && ly <= rect.height;
      if (!within) {
        if (inside && !demoRef.current) quick.current?.r(0);
        inside = false;
        return;
      }
      inside = true;
      cancelDemo();
      setTouched(true);
      const p = toMediaRef.current(lx, ly);
      aimRef.current(p.x, p.y);
    };
    const onDocLeave = () => {
      inside = false;
      if (!demoRef.current) quick.current?.r(0);
    };
    window.addEventListener('pointermove', onWinMove, { passive: true });
    document.documentElement.addEventListener('pointerleave', onDocLeave);
    return () => {
      gsap.ticker.remove(tick);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('pointermove', onWinMove);
      document.documentElement.removeEventListener('pointerleave', onDocLeave);
      cancelDemo();
      gsap.killTweensOf(pos.current);
      quick.current = null;
    };
  }, [measure]);

  const setMode = (m: DebugMode) => {
    const next = debugModeRef.current === m ? 'off' : m;
    debugModeRef.current = next;
    setDebugMode(next);
    cancelDemo();
    if (next !== 'pointer') quick.current?.r(0);
  };

  const onPointerDown = (e: React.PointerEvent) => {
    pointerType.current = e.pointerType;
    cancelDemo();
  };

  const onClick = (e: React.MouseEvent) => {
    if (pointerType.current === 'mouse' || debugModeRef.current !== 'pointer') return;
    setTouched(true);
    const rect = rootRef.current!.getBoundingClientRect();
    const p = toMedia(e.clientX - rect.left, e.clientY - rect.top);
    aim(p.x, p.y);
  };

  // Concentric pulse: inner ring scales from its own centre; positioning lives on a separate wrapper.
  const pulseRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const el = pulseRef.current;
    if (!el) return undefined;
    if (!hot || reduced) {
      gsap.killTweensOf(el);
      gsap.set(el, { scale: 1, opacity: 0 });
      return undefined;
    }
    const tw = gsap.fromTo(
      el,
      { scale: 1, opacity: 0.55, transformOrigin: '50% 50%' },
      { scale: 1.45, opacity: 0, duration: 1.3, ease: 'power2.out', repeat: -1 },
    );
    return () => { tw.kill(); };
  }, [hot, reduced]);

  const hs = geo ? toLocal(geo, HOT.x, HOT.y) : null;
  const ringSize = geo ? 70 * geo.s : 0;
  const calloutLeft = hs && geo ? hs.x + 210 < geo.w : true;
  const gap = geo && geo.w >= 768 ? 64 : 48;

  return (
    <div
      ref={rootRef}
      data-xray-slot="hero-washer"
      className="absolute inset-0 select-none overflow-hidden"
      onPointerDown={onPointerDown}
      onClick={onClick}
    >
      <div ref={mediaRef} className="absolute inset-0 will-change-transform" style={{ transformOrigin: '0 0', ['--inv' as string]: 1 }}>
        <img
          ref={imgARef}
          src={IMG_A}
          fetchPriority="high"
          alt="Premium white front-loading washing machine in a soft blue room"
          title="MENDWELL washer repair"
          draggable={false}
          onLoad={() => { loaded.current.a = true; }}
          className="absolute inset-0 h-full w-full object-cover object-[51%_20%] md:object-[51%_35%]"
        />
        <div ref={maskRef} aria-hidden="true" className="absolute inset-0" style={{ opacity: 0 }}>
          <img
            ref={imgBRef}
            src={IMG_B}
            alt=""
            draggable={false}
            onLoad={() => { loaded.current.b = true; }}
            className="absolute inset-0 h-full w-full object-cover object-[51%_20%] md:object-[51%_35%]"
          />
        </div>

        {hs && (
          <div
            aria-hidden={!hot}
            className={`pointer-events-none absolute transition-opacity duration-200 ${hot ? 'opacity-100' : 'opacity-0'}`}
            style={{ left: hs.x, top: hs.y }}
          >
            {/* Stable centred wrapper (positioning only, never animated) */}
            <div
              className="absolute"
              style={{ left: -ringSize * 0.31, top: -ringSize * 0.31, width: ringSize * 0.62, height: ringSize * 0.62 }}
            >
              <span ref={pulseRef} className="absolute inset-0 rounded-full border border-red-500" style={{ opacity: 0, transformOrigin: '50% 50%' }} />
              <span className="absolute inset-0 rounded-full border border-red-500/80" />
              <span
                className="absolute rounded-full bg-red-500"
                style={{ left: '50%', top: '50%', width: 6, height: 6, margin: -3, transform: 'scale(var(--inv))' }}
              />
            </div>
            <div className="absolute left-0 top-0" style={{ transform: 'scale(var(--inv))', transformOrigin: '0 0' }}>
              <span
                className={`absolute top-0 h-px w-12 md:w-16 bg-white/90 ${calloutLeft ? '' : '-translate-x-full'}`}
                style={{ left: calloutLeft ? ringSize * 0.31 : -ringSize * 0.31 }}
              />
              <div
                className={`absolute -translate-y-1/2 w-[150px] md:w-[170px] rounded-md bg-white px-3 py-2.5 text-left ${calloutLeft ? '' : '-translate-x-full'}`}
                style={{ left: calloutLeft ? ringSize * 0.31 + gap : -(ringSize * 0.31 + gap) }}
              >
                <p className="flex items-center gap-1.5 text-[10px] font-medium tracking-[0.14em] text-foreground-950">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                  DRAIN PUMP
                </p>
                <p className="mt-1 text-xs text-foreground-800">Possible blockage</p>
                <p className="text-[11px] text-foreground-600">Inspection recommended</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {DEBUG_ENABLED && createPortal(
        <div className="fixed bottom-4 right-4 z-[9999] w-64 rounded-md border border-background-300 bg-background-50 p-3 font-mono text-[11px] text-foreground-950">
          <p className="mb-2 font-sans text-xs font-medium">X-Ray debug</p>
          {dbg && (
            <ul className="space-y-0.5">
              <li>Image A loaded: {String(dbg.aLoaded)}</li>
              <li>Image B loaded: {String(dbg.bLoaded)}</li>
              <li>Diagnostic stage active: {String(dbg.armed)}</li>
              <li>X-Ray overlay visible: {String(dbg.visible)}</li>
              <li>Pointer (img px): {dbg.img}</li>
              <li>Mask: {dbg.mask}</li>
              <li>Pump hotspot active: {String(dbg.hot)}</li>
            </ul>
          )}
          <div className="mt-2 flex flex-col gap-1">
            {([['full', 'Show Full X-Ray'], ['fixed', 'Show Fixed Scan'], ['pointer', 'Enable Pointer Scan']] as const).map(([m, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={`cursor-pointer whitespace-nowrap rounded-md px-2 py-1 text-left font-sans text-xs ${debugMode === m ? 'bg-primary-500 text-background-50' : 'bg-background-200 text-foreground-900'}`}
              >
                {debugMode === m ? '● ' : '○ '}{label}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}